import styled from '@emotion/styled'
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image'
import React, { useEffect, useState, useMemo } from 'react'
import Video from '../Video'
import { debounce } from 'lodash'

const SectionContainerTop = styled.div`
  background-color: ${(props) => props.color};
  position: relative;
  display: flex;
  align-items: stretch;
  flex-direction: ${(props) => (props.reverse ? 'row-reverse' : 'row')};
  ${(props) =>
    props.HeightPercentage
      ? `height: calc(${props.HeightPercentage}vh - 126px);`
      : 'height: calc(100vh);'}
  @media (max-width: 768px) {
    ${(props) => (props.mobilePadding ? 'padding: 32px 0px;' : '')};
    flex-direction: ${(props) => (props.reverseMobile ? 'column-reverse' : 'column')};
    ${(props) => (!props.noMin && !props.HeightPercentage ? 'height: calc(100vh);' : '')}
  }
  @media (min-width: 1024px) {
    ${(props) => (props.minHeight ? `min-height: ${props.minHeight};` : '')}
    ${(props) => (props.negativeMargin ? 'height: calc(75vh - 126px);' : '')}
    ${(props) => (props.maxWidth ? 'max-width: 1200px; margin: 0 auto;' : '')}
  }
`
const HeaderImage = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  ${(props) => (props.padding ? 'margin: 8%;width: 34%;' : '')}
  @media (max-width: 1025px) {
    width: 100%;
    ${(props) => (props.padding ? 'width: 90%;margin: 5%;' : '')}
    ${(props) => (props.noMobile ? 'display: none;' : '')}
    ${(props) => (props.fullLength ? 'width: 100%;height: calc(100vh - 110px);' : '')}
  }

  /* Smooth image loading transitions */
  img {
    transition: opacity 0.2s ease-in-out;
  }
`
const HeaderVideo = styled(Video)`
  width: 100%;
  height: 100%;
  ${(props) => (props.padding ? 'margin: 8%;width: 34%;' : '')}
  @media (max-width: 1025px) {
    width: 100%;
    ${(props) => (props.padding ? 'width: 90%;margin: 5%;' : '')}
    ${(props) => (props.noMobile ? 'display: none;' : '')}
    ${(props) => (props.fullLength ? 'width: 100%;height: calc(100vh - 110px);' : '')}
  }
`
const SectionCopy = styled.div`
  width: ${(props) => (props.short ? '45%;' : '50%;')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  background-color: rgb(var(--${(props) => props.color}));

  @media (min-width: 1026px) {
    ${({ trunk }) => trunk && `align-items: flex-end; text-align: center; padding-right: 10%;`}
    ${({ school }) => school && `align-items: flex-start; text-align: left; padding-left: 10%;`}
  }
  @media (max-width: 1025px) {
    width: 100%;
    ${({ noMin }) => noMin && 'height : 100%;'}

    text-align: left;
    z-index: 2;
    ${(props) => (props.left ? 'align-items: flex-start;' : '')};
    ${(props) => (props.lower ? 'padding: 0 32px;' : '')};
    ${(props) => (props.noTopPadding ? 'padding-top: 0;' : '')}
    ${(props) =>
    props.center ? 'text-align: center; align-items: center;justify-content: center;' : ''}
    ${({ trunk }) => trunk && `align-items: center;justify-content: center; text-align: center; `}
    ${(props) => (props.mobileAbsolute ? 'position: absolute; top: 22px; padding-top: 8px;' : '')}
  }
  @media (max-width: 768px) {
    ${({ noMin }) => !noMin && 'height : 60vh;'}
  }
  @media (max-width: 320px) {
    ${({ noMin }) => !noMin && 'height : 50vh;'}
  }
`
const StaticHeaderImage = styled.img`
  width: 100%;
  height: 100%;
  ${(props) => (props.padding ? 'margin: 8%;width: 34%;' : '')}
  @media (max-width: 1025px) {
    height: calc(100vh - 110px);
    width: 100%;
    ${(props) => (props.padding ? 'width: 90%;margin: 5%;' : '')}
    ${(props) => (props.noMobile ? 'display: none;' : '')}
    ${(props) => (props.fullLength ? 'width: 100%;' : '')}
  }
  object-fit: cover;
  
  /* Smooth image loading transitions */
  transition: opacity 0.2s ease-in-out;
`

const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState('desktop');

  useEffect(() => {
    const getBreakpoint = (width) => {
      if (width <= 768) return 'mobile';
      if (width <= 1024) return 'tablet';
      return 'desktop';
    };

    const handleResize = debounce(() => {
      const currentBreakpoint = getBreakpoint(window.innerWidth);
      setBreakpoint(prev => prev !== currentBreakpoint ? currentBreakpoint : prev);
    }, 100);

    // Set initial breakpoint
    setBreakpoint(getBreakpoint(window.innerWidth));
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};

const TwoAssetsBanner = React.memo(({ data }) => {
  const breakpoint = useBreakpoint();

  // Memoize asset data to prevent unnecessary re-renders
  const assetData = useMemo(() => {
    return {
      secondAsset: {
        desktop: data.SecondAsset,
        mobile: data.MobileSecondAsset,
        current: breakpoint === 'desktop' ? data.SecondAsset : data.MobileSecondAsset
      },
      firstAsset: {
        desktop: data.FirstAsset,
        mobile: data.MobileFirstAsset,
        current: breakpoint === 'desktop' ? data.FirstAsset : data.MobileFirstAsset
      }
    };
  }, [data.SecondAsset, data.MobileSecondAsset, data.FirstAsset, data.MobileFirstAsset, breakpoint]);

  return (
    <SectionContainerTop>
      <SectionCopy style={{ height: breakpoint === 'mobile' && '100vh' }}>
        {(assetData.secondAsset.desktop?.isStatic && assetData.secondAsset.mobile?.isStatic) ? (
          <StaticHeaderImage src={breakpoint === 'desktop' ? assetData.secondAsset.desktop?.url : assetData.secondAsset.mobile?.url} />
        ) : (
          assetData.secondAsset.current?.mime?.includes('video') ? (
            <HeaderVideo
              alt={assetData.secondAsset.current?.alternativeText}
              src={assetData.secondAsset.current?.file?.publicURL}
            />
          ) : (
            (assetData.secondAsset.desktop?.file?.childImageSharp?.gatsbyImageData && assetData.secondAsset.mobile?.file?.childImageSharp?.gatsbyImageData) && (
              <HeaderImage
                alt={assetData.secondAsset.desktop?.alternativeText}
                image={withArtDirection(
                  getImage(assetData.secondAsset.desktop?.file?.childImageSharp?.gatsbyImageData),
                  [
                    {
                      media: '(max-width: 1024px)',
                      image: getImage(
                        assetData.secondAsset.mobile?.file?.childImageSharp?.gatsbyImageDataMobile
                      ),
                    },
                  ]
                )}
                style={{ objectFit: 'contain' }}
              />
            )
          )
        )}
      </SectionCopy>
      <SectionCopy style={{ display: breakpoint === 'mobile' && 'none' }}>
        {(assetData.firstAsset.desktop?.isStatic && assetData.firstAsset.mobile?.isStatic) ? (
          <StaticHeaderImage src={breakpoint === 'desktop' ? assetData.firstAsset.desktop?.url : assetData.firstAsset.mobile?.url} />
        ) : (assetData.firstAsset.current?.mime?.includes('video') ? (
          <HeaderVideo
            alt={assetData.firstAsset.current?.alternativeText}
            src={assetData.firstAsset.current?.file?.publicURL}
          />
        ) : (
          (assetData.firstAsset.desktop?.file?.childImageSharp?.gatsbyImageData && assetData.firstAsset.mobile?.file?.childImageSharp?.gatsbyImageData) && (
            <HeaderImage
              alt={assetData.firstAsset.desktop?.alternativeText}
              image={withArtDirection(
                getImage(assetData.firstAsset.desktop?.file?.childImageSharp?.gatsbyImageData),
                [
                  {
                    media: '(max-width: 1024px)',
                    image: getImage(
                      assetData.firstAsset.mobile?.file?.childImageSharp?.gatsbyImageDataMobile
                    ),
                  },
                ]
              )}
              style={{ objectFit: 'contain' }}
            />
          )
        ))}
      </SectionCopy>
    </SectionContainerTop>
  )
});

export default TwoAssetsBanner
